import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useCallback} from 'react'

export interface UseCollapsibleSidebarStateOptions {
  /**
   * The sessionStorage key used to persist the expanded/collapsed state.
   */
  storageKey: string
  /**
   * The expanded state to use when there is no persisted value (or when the
   * environment cannot read sessionStorage, e.g., during SSR).
   */
  defaultExpanded?: boolean
}

export interface CollapsibleSidebarState {
  isExpanded: boolean
  toggle: () => void
  setExpanded: (expanded: boolean | ((prev: boolean) => boolean)) => void
}

/**
 * Manages the expanded/collapsed state of a sidebar with sessionStorage
 * persistence. Uses `useSessionStorage` from `@github-ui/use-safe-storage`
 * for SSR-safe reads via `useSyncExternalStore` and cross-component sync.
 */
export function useCollapsibleSidebarState({
  storageKey,
  defaultExpanded = true,
}: UseCollapsibleSidebarStateOptions): CollapsibleSidebarState {
  const [isExpanded, setStoredExpanded] = useSessionStorage(storageKey, defaultExpanded)

  const setExpanded = useCallback(
    (expanded: boolean | ((prev: boolean) => boolean)) => {
      setStoredExpanded(expanded)
    },
    [setStoredExpanded],
  )

  const toggle = useCallback(() => {
    setStoredExpanded(prev => !prev)
  }, [setStoredExpanded])

  return {isExpanded, toggle, setExpanded}
}
